import { deepCopy } from '@utils/common';

const DefaultDataWhenNull = {
  board: 'NA',
  community: 'NA',
  communityDevPhilanthropy: 'NA',
  compensationBenefits: 'NA',
  diversityLaborRights: 'NA',
  employee: 'NA',
  energyClimateChange: 'NA',
  environment: 'NA',
  environmentPolicyReporting: 'NA',
  governance: 'NA',
  humanRightsSupplyChain: 'NA',
  leadershipEthics: 'NA',
  product: 'NA',
  resourceManagement: 'NA',
  trainingHealthSafety: 'NA',
  transparencyReporting: 'NA',
  overall: null,
  isOverallNotApplicable: true,
};

const getRelativeValue = (csr, point2, point3) =>
  Object.keys(csr).reduce((acc, k) => {
    if (
      ['industry', 'companyName', 'overall', 'isOverallNotApplicable'].includes(
        k,
      )
    ) {
      return acc;
    }
    const value = csr[k],
      average = point2[k],
      above = point3[k];
    let val;
    if (value === null || average === null || above === null) {
      val = 'NA';
    } else if (value < average) {
      val = 'Below';
    } else if (value < above) {
      val = 'Industry';
    } else {
      val = 'Above';
    }
    return Object.assign({}, acc, { [k]: val });
  }, {});

const calculateESGData = (dataSrc, isEditView = false) => {
  const data = deepCopy(dataSrc);
  let buyerInfo, targetInfo;
  const defaultPointValue = [0, 37.5, 62.5, 100];
  const overallInfo = [
    {
      companyName: data?.company?.buyerCsr?.companyName || '-',
      score: data?.company?.buyerCsr?.overall || 0,
      isOverallNotApplicable: data?.company?.buyerCsr?.overall === null,
      p1:
        data?.company?.buyerContrastCsr?.[0]?.point1?.overall ||
        defaultPointValue[0],
      p2:
        data?.company?.buyerContrastCsr?.[0]?.point2?.overall ||
        defaultPointValue[1],
      p3:
        data?.company?.buyerContrastCsr?.[0]?.point3?.overall ||
        defaultPointValue[2],
      p4:
        data?.company?.buyerContrastCsr?.[0]?.point4?.overall ||
        defaultPointValue[3],
    },
    {
      companyName: data?.company?.targetCsr?.companyName || '-',
      score: data?.company?.targetCsr?.overall || 0,
      isOverallNotApplicable: data?.company?.targetCsr?.overall === null,
      p1:
        data?.company?.targetContrastCsr?.[0]?.point1?.overall ||
        defaultPointValue[0],
      p2:
        data?.company?.targetContrastCsr?.[0]?.point2?.overall ||
        defaultPointValue[1],
      p3:
        data?.company?.targetContrastCsr?.[0]?.point3?.overall ||
        defaultPointValue[2],
      p4:
        data?.company?.targetContrastCsr?.[0]?.point4?.overall ||
        defaultPointValue[3],
    },
  ];

  if (isEditView && data?.company?.buyerUDCsr !== null) {
    overallInfo[0].score = data?.company?.buyerUDCsr?.overall || 0;
    overallInfo[1].score = data?.company?.targetUDCsr?.overall || 0;
    overallInfo[0].isOverallNotApplicable =
      typeof data?.company?.buyerUDCsr?.isOverallNotApplicable === 'boolean'
        ? data.company.buyerUDCsr.isOverallNotApplicable
        : false;
    overallInfo[1].isOverallNotApplicable =
      typeof data?.company?.targetUDCsr?.isOverallNotApplicable === 'boolean'
        ? data.company.targetUDCsr.isOverallNotApplicable
        : false;

    return {
      buyerInfo: data?.company?.buyerUDCsr || DefaultDataWhenNull,
      targetInfo: data?.company?.targetUDCsr || DefaultDataWhenNull,
      overallInfo,
    };
  }

  try {
    const {
      company: { buyerContrastCsr, buyerCsr },
    } = data;

    const [{ point2: buyerPoint2, point3: buyerPoint3 }] = buyerContrastCsr;
    buyerInfo = {
      ...buyerCsr,
      ...getRelativeValue(buyerCsr, buyerPoint2, buyerPoint3),
    };
  } catch (e) {
    buyerInfo = { ...DefaultDataWhenNull };
  }

  try {
    const {
      company: { targetContrastCsr, targetCsr },
    } = data;

    const [{ point2: targetPoint2, point3: targetPoint3 }] = targetContrastCsr;
    targetInfo = {
      ...targetCsr,
      ...getRelativeValue(targetCsr, targetPoint2, targetPoint3),
    };
  } catch (e) {
    targetInfo = { ...DefaultDataWhenNull };
  }

  return {
    overallInfo,
    buyerInfo,
    targetInfo,
  };
};

export { calculateESGData };
